// src/pages/settings/SettingsLayout.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  UserCircleIcon,
  BellAlertIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/context/AuthContext'

const ALL_SECTIONS = [
  { name: 'Profile', path: '/settings/profile', icon: UserCircleIcon },
  { name: 'Notifications', path: '/settings/notifications', icon: BellAlertIcon },
  { name: 'Billing', path: '/settings/billing', icon: CreditCardIcon },
]

export default function SettingsLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { userProfile } = useAuth()
  const [current, setCurrent] = useState(location.pathname)

  // Parents have no Scrimly subscription, so the Billing tab is irrelevant
  // (it's also coach-flavored — Stripe Connect, plan upgrades, etc.).
  const isParent = String(userProfile?.role || '').toLowerCase() === 'parent'
  const personalSections = useMemo(
    () => (isParent ? ALL_SECTIONS.filter((s) => s.path !== '/settings/billing') : ALL_SECTIONS),
    [isParent]
  )

  useEffect(() => {
    setCurrent(location.pathname)
  }, [location.pathname])

  // If a parent lands on /settings/billing via direct URL, bounce to Profile.
  useEffect(() => {
    if (isParent && location.pathname === '/settings/billing') {
      navigate('/settings/profile', { replace: true })
    }
  }, [isParent, location.pathname, navigate])

  const renderLink = ({ name, path, icon: Icon }) => {
    const active = current === path || current.startsWith(path + '/')

    return (
      <Link
        key={path}
        to={path}
        onClick={() => setCurrent(path)}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition',
          active
            ? 'text-[#3d6010] bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40'
            : 'text-zinc-600 hover:bg-zinc-100'
        )}
      >
        <Icon className="h-4 w-4" />
        {name}
      </Link>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Section nav row (desktop + tablet) */}
      <div className="hidden sm:flex flex-wrap gap-2">
        {personalSections.map(renderLink)}
      </div>

      {/* Dropdown (mobile) */}
      <div className="sm:hidden">
        <select
          className="w-full p-2 rounded-md bg-zinc-100 text-sm"
          value={current}
          onChange={(e) => {
            const path = e.target.value
            setCurrent(path)
            navigate(path)
          }}
        >
          {personalSections.map(({ name, path }) => (
            <option key={path} value={path}>{name}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      <div className="min-h-0">
        <Outlet />
      </div>
    </div>
  )
}
